<template>
    <div class="items-list-container">
        <div class="list-header">
            <h6 class="list-title">Pilares</h6>
            <slot name="header-actions"></slot>
        </div>

        <div class="items-tree">
            <!-- Pilar -->
            <div v-for="pillar in pillars" :key="'pillar-' + pillar.id" class="tree-level pillar-level">
                <div class="tree-item pillar-item" :style="{ borderLeftColor: pillar.color || '#6c757d' }"
                    @click="togglePillar(pillar.id)">
                    <i class="fa" :class="isPillarExpanded(pillar.id) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                    <span class="item-name">{{ pillar.name }}</span>
                    <span class="plan-count">{{ getPillarPlanCount(pillar) }} planes</span>
                </div>

                <!-- Elementos -->
                <div v-if="isPillarExpanded(pillar.id)" class="tree-children">
                    <div v-for="element in pillar.elements" :key="'element-' + element.id"
                        class="tree-level element-level">
                        <div class="tree-item element-item" :style="{ borderLeftColor: element.color || '#9ca3af' }"
                            @click="toggleElement(element.id)">
                            <i class="fa"
                                :class="isElementExpanded(element.id) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                            <span class="item-name">{{ element.name }}</span>
                            <span class="plan-count">{{ getElementPlanCount(element) }} planes</span>
                        </div>

                        <!-- Items Generales -->
                        <div v-if="isElementExpanded(element.id)" class="tree-children">
                            <div v-for="generalItem in element.general_items" :key="'general-' + generalItem.id"
                                class="tree-level general-item-level">
                                <div class="tree-item general-item" @click="toggleGeneralItem(generalItem.id)">
                                    <i class="fa"
                                        :class="isGeneralItemExpanded(generalItem.id) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                                    <span class="item-name">{{ generalItem.name }}</span>
                                </div>

                                <!-- Items con planes de acción -->
                                <div v-if="isGeneralItemExpanded(generalItem.id)" class="tree-children">
                                    <div v-for="item in generalItem.items" :key="'item-' + item.id"
                                        class="tree-level item-level">
                                        <div class="item-card" :class="{
                                            'active': selectedItemId === item.id
                                        }" @click="selectItem(item, pillar, element)">
                                            <div class="item-card-header">
                                                <span class="priority-pill" :style="getPriorityStyle(item.plan_action)">
                                                    <i :class="'fa ' + getPriorityIcon(item.plan_action)"
                                                        class="mr-1"></i>
                                                    {{ getPriorityLabel(item.plan_action) }}
                                                </span>
                                            </div>
                                            <div class="item-card-body">
                                                <p class="item-question">{{ item.name }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Empty state -->
        <div v-if="pillars.length === 0" class="empty-state">
            <i class="fa fa-filter fa-3x"></i>
            <p>No se encontraron planes de acción con los filtros aplicados</p>
            <p class="empty-hint">Intenta ajustar o limpiar los filtros</p>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ActionPlanItemsList',
    props: {
        pillars: {
            type: Array,
            required: true,
            default: () => []
        },
        selectedItemId: {
            type: [Number, String],
            default: null
        },
        statusDefinitions: {
            type: Array,
            default: () => []
        },
        priorityDefinitions: {
            type: Array,
            default: () => [
                { value: 'Alto', label: 'Alto', color: '#DC2626', bg_color: '#FEE2E2', text_color: '#991B1B', icon: 'fa-arrow-up' },
                { value: 'Medio', label: 'Medio', color: '#F59E0B', bg_color: '#FEF3C7', text_color: '#92400E', icon: 'fa-minus' },
                { value: 'Bajo', label: 'Bajo', color: '#22C55E', bg_color: '#D1FAE5', text_color: '#065F46', icon: 'fa-arrow-down' }
            ]
        }
    },
    data() {
        return {
            expandedPillar: null,
            expandedElement: null,
            expandedGeneralItem: null
        };
    },
    watch: {
        pillars: {
            immediate: true,
            handler(newPillars) {
                if (newPillars.length > 0 && !this.expandedPillar) {
                    this.expandedPillar = newPillars[0].id;

                    if (newPillars[0].elements && newPillars[0].elements.length > 0) {
                        this.expandedElement = newPillars[0].elements[0].id;

                        if (newPillars[0].elements[0].general_items && newPillars[0].elements[0].general_items.length > 0) {
                            this.expandedGeneralItem = newPillars[0].elements[0].general_items[0].id;

                            // Seleccionar el primer item automáticamente
                            const firstGeneralItem = newPillars[0].elements[0].general_items[0];
                            if (firstGeneralItem.items && firstGeneralItem.items.length > 0) {
                                this.$emit('item-selected', {
                                    item: firstGeneralItem.items[0],
                                    pillarName: newPillars[0].name,
                                    elementName: newPillars[0].elements[0].name
                                });
                            }
                        }
                    }
                }
            }
        }
    },
    methods: {
        isPillarExpanded(pillarId) {
            return this.expandedPillar === pillarId;
        },
        isElementExpanded(elementId) {
            return this.expandedElement === elementId;
        },
        isGeneralItemExpanded(generalItemId) {
            return this.expandedGeneralItem === generalItemId;
        },
        togglePillar(pillarId) {
            this.expandedPillar = this.expandedPillar === pillarId ? null : pillarId;
            if (this.expandedPillar !== pillarId) {
                this.expandedElement = null;
                this.expandedGeneralItem = null;
            }
        },
        toggleElement(elementId) {
            this.expandedElement = this.expandedElement === elementId ? null : elementId;
            if (this.expandedElement !== elementId) {
                this.expandedGeneralItem = null;
            }
        },
        toggleGeneralItem(generalItemId) {
            this.expandedGeneralItem = this.expandedGeneralItem === generalItemId ? null : generalItemId;
        },
        selectItem(item, pillar, element) {
            this.$emit('item-selected', {
                item,
                pillarName: pillar.name,
                elementName: element.name
            });
        },
        getStatusClass(planAction) {
            const status = (planAction && planAction.status) ? planAction.status : 'Abierto';
            const def = this.statusDefinitions.find(s => s.value === status);
            if (!def) return 'status-open';
            return 'status-' + status.replace(/ /g, '-');
        },
        getStatusStyle(planAction) {
            const status = (planAction && planAction.status) ? planAction.status : 'Abierto';
            const def = this.statusDefinitions.find(s => s.value === status);
            if (!def) return {};
            return {
                backgroundColor: def.bg_color,
                color: def.text_color
            };
        },
        getPillarPlanCount(pillar) {
            let count = 0;
            if (pillar.elements) {
                pillar.elements.forEach(element => {
                    if (element.general_items) {
                        element.general_items.forEach(gi => {
                            count += (gi.items && gi.items.length) || 0;
                        });
                    }
                });
            }
            return count;
        },
        getElementPlanCount(element) {
            let count = 0;
            if (element.general_items) {
                element.general_items.forEach(gi => {
                    count += (gi.items && gi.items.length) || 0;
                });
            }
            return count;
        },
        getStatusLabel(planAction) {
            const status = (planAction && planAction.status) ? planAction.status : 'Abierto';
            const def = this.statusDefinitions.find(s => s.value === status);
            return def ? def.label : status;
        },
        getPriorityStyle(planAction) {
            const priority = (planAction && planAction.priority) ? planAction.priority : 'Medio';
            const def = this.priorityDefinitions.find(p => p.value === priority);
            if (!def) return { backgroundColor: '#FEF3C7', color: '#92400E' };
            return {
                backgroundColor: def.bg_color,
                color: def.text_color
            };
        },
        getPriorityLabel(planAction) {
            const priority = (planAction && planAction.priority) ? planAction.priority : 'Medio';
            const def = this.priorityDefinitions.find(p => p.value === priority);
            return def ? def.label : priority;
        },
        getPriorityIcon(planAction) {
            const priority = (planAction && planAction.priority) ? planAction.priority : 'Medio';
            const def = this.priorityDefinitions.find(p => p.value === priority);
            return def ? def.icon : 'fa-minus';
        }
    }
};
</script>

<style scoped>
.items-list-container {
    background: white;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    height: calc(100vh - 350px);
    display: flex;
    flex-direction: column;
}

.list-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border-bottom: 1px solid #E5E7EB;
}

.list-title {
    font-size: 14px;
    font-weight: 600;
    color: #1F2937;
    margin: 0;
}

.items-tree {
    flex: 1;
    overflow-y: auto;
    padding: 8px;
}

/* Niveles del árbol */
.tree-level {
    margin-bottom: 8px;
}

.pillar-level {
    margin-bottom: 12px;
}

.element-level {
    margin-bottom: 8px;
}

.tree-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    cursor: pointer;
    border-radius: 6px;
    transition: all 0.15s ease;
    user-select: none;
}

.tree-item i {
    font-size: 12px;
    color: #6B7280;
    width: 12px;
    flex-shrink: 0;
}

.item-name {
    flex: 1;
    font-size: 14px;
    color: #1F2937;
    line-height: 1.4;
    white-space: normal;
}

.plan-count {
    font-size: 12px;
    color: #6B7280;
    white-space: nowrap;
}

/* Pilar */
.pillar-item {
    font-weight: 700;
    text-transform: uppercase;
    font-size: 12px;
    letter-spacing: 0.5px;
    background-color: white !important;
    border-top: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    border-bottom: 1px solid #E5E7EB;
    border-left: 5px solid;
    border-radius: 6px;
    padding: 12px !important;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.pillar-item:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

/* Elemento */
.element-item {
    font-weight: 600;
    margin-left: 16px;
    background-color: white !important;
    border-top: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    border-bottom: 1px solid #E5E7EB;
    border-left: 4px solid;
    border-radius: 6px;
    padding: 10px 12px !important;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.element-item:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

/* Item General */
.general-item {
    margin-left: 32px;
    font-weight: 500;
    background-color: white;
    border: 1px solid #E5E7EB;
    border-radius: 6px;
    padding: 10px 12px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.general-item:hover {
    background-color: #F9FAFB !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.tree-children {
    margin-top: 4px;
}

/* Item card con plan de acción */
.item-card {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px;
    margin-left: 48px;
    background-color: white;
    border: 1px solid #E5E7EB;
    border-radius: 12px;
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
    cursor: pointer;
    transition: all 0.15s ease;
    margin-bottom: 6px;
}

.item-card:hover {
    border-color: #10B981;
    box-shadow: 0 6px 16px rgba(16, 185, 129, 0.15);
}

.item-card.active {
    border-color: #0f9d74;
    box-shadow: 0 6px 18px rgba(16, 185, 129, 0.18);
}

.item-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    margin-bottom: 0;
}

.status-pill,
.priority-pill {
    padding: 2px 9px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    white-space: normal;
    line-height: 1.2;
    max-width: 200px;
    display: inline-flex;
    align-items: center;
}

.priority-pill i {
    font-size: 10px;
}

.item-card-body {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.item-question {
    font-size: 13px;
    color: #374151;
    margin: 0;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.item-card.active .item-question {
    color: #065F46;
}

/* Empty state */
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    color: #9CA3AF;
    text-align: center;
}

.empty-state i {
    margin-bottom: 16px;
    opacity: 0.5;
}

.empty-state p {
    margin: 0;
    font-size: 14px;
}

.empty-hint {
    font-size: 12px !important;
    margin-top: 8px !important;
    opacity: 0.7;
}

/* Scrollbar */
.items-tree::-webkit-scrollbar {
    width: 4px;
}

.items-tree::-webkit-scrollbar-track {
    background: #F3F4F6;
}

.items-tree::-webkit-scrollbar-thumb {
    background: #C7CCD5;
    border-radius: 4px;
}

.items-tree::-webkit-scrollbar-thumb:hover {
    background: #AEB4C0;
}
</style>
